/**
 * t671u: the unpinned (auto-hide) sidebar. The icon rail never moves or resizes: the area pages panel opens as an
 * overlay to the right of the rail, so an icon under the pointer stays put and a click on it works straight away.
 * The panel opens after a short hover over the rail or panel (hover intent, so passing the pointer across doesn't
 * flash it), closes a moment after the pointer leaves both, and opens at once when keyboard focus moves in.
 */
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import clsx from 'clsx';

export const FLYOUT_OPEN_MS = 250;
export const FLYOUT_CLOSE_MS = 250;

export function useFlyoutIntent(setOpen: (v: boolean) => void) {
  const openT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = useCallback(() => {
    if (openT.current) { clearTimeout(openT.current); openT.current = null; }
    if (closeT.current) { clearTimeout(closeT.current); closeT.current = null; }
  }, []);
  useEffect(() => clear, [clear]);
  return {
    enter: useCallback(() => { if (closeT.current) { clearTimeout(closeT.current); closeT.current = null; } if (!openT.current) openT.current = setTimeout(() => { openT.current = null; setOpen(true); }, FLYOUT_OPEN_MS); }, [setOpen]),
    leave: useCallback(() => { if (openT.current) { clearTimeout(openT.current); openT.current = null; } if (!closeT.current) closeT.current = setTimeout(() => { closeT.current = null; setOpen(false); }, FLYOUT_CLOSE_MS); }, [setOpen]),
    focus: useCallback(() => { clear(); setOpen(true); }, [clear, setOpen]),
    clear,
  };
}

export function RailFlyout({ open, setOpen, rail, panel }: { open: boolean; setOpen: (v: boolean) => void; rail: ReactNode; panel: ReactNode }) {
  const intent = useFlyoutIntent(setOpen);
  const box = useRef<HTMLDivElement>(null);
  // A click focuses the icon too; only keyboard focus opens the panel at once (a click just navigates).
  const pointer = useRef(false);
  return (
    // The wrapper is the rail's width and never changes; the panel hangs off its right edge.
    <div ref={box} className="relative z-40 h-full w-14 shrink-0" data-testid="sidebar-autohide"
      onMouseEnter={intent.enter} onMouseLeave={intent.leave}
      onPointerDown={() => { pointer.current = true; }} onPointerUp={() => { setTimeout(() => { pointer.current = false; }, 0); }}
      onFocus={() => { if (!pointer.current) intent.focus(); }}
      onBlur={e => { if (!box.current?.contains(e.relatedTarget as Node | null)) intent.leave(); }}>
      {rail}
      <div className={clsx('absolute inset-y-0 left-full shadow-2xl transition-[opacity,transform] duration-150', open ? 'translate-x-0 opacity-100' : 'pointer-events-none invisible -translate-x-2 opacity-0')}
        data-testid="sidebar-overlay" aria-hidden={!open}>
        {panel}
      </div>
    </div>
  );
}
